import RestX from 'highered-utils/restx';
import i18n from '~/i18n';

// Utils
import asyncForEach from '~/utils/asyncForEach';
import hasLangChanged from '~/utils/languages/hasLangChanged';
import findDeletedEntities from '~/utils/findDeletedEntities';

class CandidateService {
    static async saveWorkAuthorizations(workAuthorizations, oldWorkAuthorizations) {
        const errors = [];

        const addItems = [];
        const deleteItems = [];

        oldWorkAuthorizations
            .filter(w => w.implicit === false)
            .forEach(a => {
                const found = workAuthorizations.find(w => w.id === a.id);
                if (found === undefined) {
                    deleteItems.push(a);
                }
            });

        workAuthorizations
            .filter(w => w.implicit === false)
            .forEach(a => {
                const found = oldWorkAuthorizations.find(
                    w => w.code === a.code && w.type === a.type
                );
                if (found === undefined) {
                    addItems.push(a);
                }
            });

        await asyncForEach(addItems, async workAuthorization => {
            await RestX.getInterfaces()
                .candidate.workauthorization.post({
                    body: workAuthorization,
                })
                .catch(err => errors.push(err));
        });

        await asyncForEach(deleteItems, async workAuthorization => {
            await RestX.getInterfaces()
                .candidate.workauthorization.id(workAuthorization.id)
                .delete({ body: {} })
                .catch(err => errors.push(err));
        });

        if (errors.length > 0) {
            return Promise.reject(errors);
        }
        return Promise.resolve();
    }

    static async saveLanguages(languages, oldLanguages) {
        const errors = [];

        await asyncForEach(languages, async lang => {
            // Check if language has changed or not
            const hasChanged = hasLangChanged(lang, oldLanguages);
            const body = {
                languageId: lang.languageId,
                proficiencyId: lang.proficiencyId,
            }; // The payload to send
            if (lang.id && hasChanged && (body.languageId || body.proficiencyId)) {
                // Identifer exists in language => update language
                await RestX.getInterfaces()
                    .candidate.language.id(lang.id)
                    .put({
                        body: body,
                    })
                    .catch(err => errors.push(err));
            } else if (!lang.id && body.languageId && body.proficiencyId) {
                // No identifer in language => create new language
                await RestX.getInterfaces()
                    .candidate.language.post({
                        body: body,
                    })
                    .catch(err => errors.push(err));
            }
        }).catch(err => errors.push(err));

        // Check if some languages has been deleted
        const deletedLangs = findDeletedEntities(languages, oldLanguages);

        // Delete removed languages
        await asyncForEach(deletedLangs, async langId => {
            await RestX.getInterfaces()
                .candidate.language.id(langId)
                .delete({ body: {} })
                .catch(err => errors.push(err));
        }).catch(err => errors.push(err));

        // Reject if errors, otherwise resolve
        if (errors.length > 0) {
            return Promise.reject(errors);
        }
        return Promise.resolve();
    }

    static async saveUserData(userData) {
        return RestX.getInterfaces().user.me.put({
            body: userData,
        });
    }

    static async saveUserImage(image) {
        let formData = new FormData();
        formData.append('avatar', image);
        await RestX.getInterfaces()
            .user.avatar.post({
                body: formData,
            })
            .then(() => {})
            .catch(error => {
                console.log(error);
                RestX.getInterfaces().dialogModal.openModal({
                    title: i18n.t('spotlight.errors.imageUpload.title'),
                    content: i18n.t('spotlight.errors.imageUpload.message'),
                    isError: true,
                });
            });
    }

    static async uploadCV(file) {
        let formData = new FormData();
        formData.append('file', file);
        await RestX.getInterfaces()
            .candidate.cv.create.post({
                body: formData,
            })
            .then(() => {})
            .catch(error => {
                console.log(error);
                RestX.getInterfaces().dialogModal.openModal({
                    title: i18n.t(''),
                    content: i18n.t(''),
                    isError: true,
                });
            });
    }

    static async deleteCV(id) {
        await RestX.getInterfaces()
            .candidate.cv.delete.id(id)
            .delete({ body: {} })
            .then(() => {})
            .catch(error => {
                console.log(error);
                RestX.getInterfaces().dialogModal.openModal({
                    title: i18n.t(''),
                    content: i18n.t(''),
                    isError: true,
                });
            });
    }

    static async setDefaultCV(id) {
        await RestX.getInterfaces()
            .candidate.cv.setdefault.id(id)
            .post({ body: {} })
            .then(() => {})
            .catch(error => {
                console.log(error);
                RestX.getInterfaces().dialogModal.openModal({
                    title: i18n.t(''),
                    content: i18n.t(''),
                    isError: true,
                });
            });
    }

    static async answerProfileSharingRequest(requestId, consent) {
        return await RestX.getInterfaces()
            .candidate.share.put({
                body: [
                    {
                        action: 'CONSENT',
                        request: requestId,
                        consented: Boolean(consent),
                        // duration: 24,
                    },
                ],
            })
            .then(res => {
                if (!res.results || res.results.length === 0) {
                    return null;
                }
                return res.results[0];
            })
            .catch(console.error);
    }

    static async saveSummary(summary) {
        const body = { summary: summary };
        return RestX.getInterfaces()
            .candidate.me.put({
                body: body,
            })
            .catch(console.error);
    }

    static async saveMe(data) {
        return RestX.getInterfaces()
            .candidate.me.put({
                body: data,
            })
            .catch(console.error);
    }
}

export default CandidateService;
